import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Mic, Save } from 'lucide-react';
import { toast } from 'sonner';

export function RecordingPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const recordingPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'recording');
  const isEnabled = recordingPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'recording') : pluginApi.disablePlugin(serverId, 'recording'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'recording', config),
    {
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']]
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    maxDurationMinutes: 120,
    autoTranscribe: false,
    retentionDays: 30,
    allowDownloads: true,
    autoStartOnJoin: false,
    allowedChannelIds: '',
    bitrateKbps: 64,
    noiseSuppression: true,
    silenceTrim: true
  });

  useEffect(() => {
    if (recordingPlugin?.config) {
      setConfig({ ...config, ...recordingPlugin.config });
    }
  }, [recordingPlugin]);

  if (!recordingPlugin) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Mic className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p>Plugin non disponibile per questo server.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-cyan-500/20 rounded-xl">
            <Mic className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Voice Recording</h3>
            <p className="text-sm text-muted-foreground">Registrazione audio dei canali vocali.</p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Durata massima (minuti)</Label>
                <Input
                  type="number"
                  min="5"
                  max="720"
                  value={config.maxDurationMinutes}
                  onChange={(e) => setConfig({ ...config, maxDurationMinutes: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Retention (giorni)</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={config.retentionDays}
                  onChange={(e) => setConfig({ ...config, retentionDays: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <Label>Trascrizione automatica</Label>
                <p className="text-xs text-muted-foreground">Genera trascrizione testuale delle registrazioni</p>
              </div>
              <Switch
                checked={config.autoTranscribe}
                onCheckedChange={(checked) => setConfig({ ...config, autoTranscribe: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <Label>Download consentiti</Label>
                <p className="text-xs text-muted-foreground">Permetti ai membri di scaricare le registrazioni</p>
              </div>
              <Switch
                checked={config.allowDownloads}
                onCheckedChange={(checked) => setConfig({ ...config, allowDownloads: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <Label>Auto-start on join</Label>
                <p className="text-xs text-muted-foreground">Avvia la registrazione quando un utente entra</p>
              </div>
              <Switch
                checked={config.autoStartOnJoin}
                onCheckedChange={(checked) => setConfig({ ...config, autoStartOnJoin: checked })}
              />
            </div>

            <div>
              <Label>Allowed Channel IDs (comma-separated)</Label>
              <Input
                value={config.allowedChannelIds}
                onChange={(e) => setConfig({ ...config, allowedChannelIds: e.target.value })}
                placeholder="123..., 456..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Bitrate (kbps)</Label>
                <Input
                  type="number"
                  min="32"
                  max="256"
                  value={config.bitrateKbps}
                  onChange={(e) => setConfig({ ...config, bitrateKbps: parseInt(e.target.value) })}
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Noise Suppression</Label>
                  <p className="text-xs text-muted-foreground">Riduzione rumore di fondo</p>
                </div>
                <Switch
                  checked={config.noiseSuppression}
                  onCheckedChange={(checked) => setConfig({ ...config, noiseSuppression: checked })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <Label>Silence Trim</Label>
                <p className="text-xs text-muted-foreground">Taglia i silenzi lunghi</p>
              </div>
              <Switch
                checked={config.silenceTrim}
                onCheckedChange={(checked) => setConfig({ ...config, silenceTrim: checked })}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => updateConfig(config)} disabled={saving} className="min-w-[160px]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
